import { BlockEmbed } from 'quill/blots/block';
import React, { useMemo } from 'react';
import { useAppDispatch } from '../../../app/redux/useAppSelector';
import { MenuItem, MenuSeparator } from '../../../components/Menu';
import expandIcon from '../../../icons/non-prefilled/16x16/expand.svg';
import newTabIcon from '../../../icons/non-prefilled/16x16/newTab.svg';
import { miscToasts } from '../../../toasts/toasts';
import { UIActions } from '../../../ui/uiSlice';
import useIsPublicView from '../../../utils/useIsPublicView';
import { useHoveredBlotListener } from '../../HoveredBlotListener';
import type { HoveredBlotListenerSetting } from '../../HoveredBlotListener';
import { useQuill, useQuillReadOnly } from '../../QuillContext';
import { getEmbedPresentationOptions } from '../../embed/EmbedComponent';
import type { EmbedValue } from '../../embed/matchEmbed';
import { EmbedMode, embedUrl } from '../../embed/matchEmbed';
import { DOCUMENT_LIKE_TYPES } from '../../formats/Embed';
import EDITOR_INTEGRATION_MENTIONS from '../../mentions/EditorIntegrationMentions';
import EditorModal from '../../modals/EditorModal';
import ConvertIntoCardMenuItem from '../ConvertIntoCardMenuItem';
import ConvertIntoLinkMenuItem from '../ConvertIntoLinkMenuItem';
import ConvertIntoMentionMenuItem from '../ConvertIntoMentionMenuItem';
import CopyLinkMenuItem from '../CopyLinkMenuItem';
import OpenInNewTabMenuItem from '../OpenInNewTabMenuItem';
import { copyIcon } from '../editorIcons';
import DeleteMenuItem from './DeleteMenuItem';
import FloatingToolbar from './FloatingToolbar';
import Toolbar, {
  ToolbarOverflowMenu,
  useToolbarOverflowMenuClose,
} from './Toolbar';
import ToolbarButton, { ToolbarButtonGroup } from './buttons/ToolbarButton';
import ToolbarCommentButtonGroup from './buttons/ToolbarCommentButtonGroup';
import ToolbarHeightButtonGroup from './buttons/ToolbarHeightButtonGroup';
import ToolbarSpanButtonGroup from './buttons/ToolbarSpanButtonGroup';
import formatBlotWithoutScrolling from './formatBlotWithoutScrolling';

const unaccessibleFullUrl = ['google-map'];

type Formats = Record<string, string[] | string | null | undefined> & {
  comment?: string[] | null;
};

type Value = { embed?: EmbedValue; iframe?: string };

function isDocumentLike(value: Value) {
  return value.embed ? DOCUMENT_LIKE_TYPES.has(value.embed.type) : false;
}

function isFullUrlAccessible(value: Value) {
  return !value.embed || !unaccessibleFullUrl.includes(value.embed.type);
}

interface ViewExpandedMenuItemProps {
  onClick: () => void;
}

const ViewExpandedMenuItem = ({ onClick }: ViewExpandedMenuItemProps) => {
  const closeOverflowMenu = useToolbarOverflowMenuClose();
  return (
    <MenuItem
      icon={expandIcon}
      onClick={() => {
        onClick();
        closeOverflowMenu();
      }}
    >
      View expanded
    </MenuItem>
  );
};

interface EmbedToolbarOverflowMenuItemsProps {
  value: Value;
  blot: BlockEmbed;
  onViewExpanded: () => void;
  fullUrl: string | false | undefined;
}

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const EmbedToolbarOverflowMenuItems = ({
  value,
  blot,
  fullUrl,
  onViewExpanded,
}: EmbedToolbarOverflowMenuItemsProps) => {
  const { quill } = useQuill();
  const canBeRenderedAsMention = Boolean(
    value.embed &&
      (value.embed.type === 'post' ||
        EDITOR_INTEGRATION_MENTIONS[value.embed.type]?.modes.includes(
          'mention',
        )),
  );

  const canBeRenderedAsCard = useMemo(() => {
    return (
      value.embed && value.embed.type === 'post' && quill?.scroll.query('card')
    );
  }, [quill, value]);

  return (
    <>
      {!isDocumentLike(value) && (
        <ViewExpandedMenuItem onClick={onViewExpanded} />
      )}
      {isFullUrlAccessible(value) && fullUrl && (
        <OpenInNewTabMenuItem link={fullUrl} />
      )}
      {quill && (
        <>
          {canBeRenderedAsMention && value.embed && (
            <ConvertIntoMentionMenuItem
              quill={quill}
              blot={blot}
              type={value.embed.type}
              id={value.embed.id}
              params={value.embed.params}
            />
          )}
          {canBeRenderedAsCard && (
            <ConvertIntoCardMenuItem quill={quill} blot={blot} />
          )}
          {fullUrl && (
            <ConvertIntoLinkMenuItem quill={quill} blot={blot} link={fullUrl} />
          )}
        </>
      )}
      {fullUrl && <CopyLinkMenuItem link={fullUrl} />}
      <MenuSeparator />
      <DeleteMenuItem onClick={() => blot.remove()}>
        Remove embed
      </DeleteMenuItem>
    </>
  );
};

interface BaseEmbedToolbarProps {
  hoveredBlot: BlockEmbed;
  formats: Formats;
  format: (format: { height?: string | null; span?: string | null }) => void;
  setPersist: (persist: boolean) => void;
  value: Value;
}

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const BaseEmbedToolbar = ({
  hoveredBlot,
  formats,
  format,
  setPersist,
  value,
}: BaseEmbedToolbarProps) => {
  const readOnly = useQuillReadOnly();
  const dispatch = useAppDispatch();
  const isPublicView = useIsPublicView();

  const fullUrl = value.embed
    ? embedUrl(value.embed, EmbedMode.FULL)
    : value.iframe;

  const modalProps = value.embed
    ? {
        embed: {
          id: value.embed.id,
          type: value.embed.type,
          readOnly,
        },
      }
    : { iframe: value.iframe };

  if (readOnly && isPublicView) {
    const canOpenLink = !!fullUrl && isFullUrlAccessible(value);
    return (
      <Toolbar>
        {canOpenLink && (
          <ToolbarButtonGroup>
            <ToolbarButton
              icon={newTabIcon}
              tooltip="Open in new tab"
              to={fullUrl}
              newTab
            >
              Open
            </ToolbarButton>
          </ToolbarButtonGroup>
        )}
        {!isDocumentLike(value) && (
          <ToolbarButtonGroup>
            <ToolbarButton
              icon={expandIcon}
              tooltip="View expanded"
              onClick={() =>
                dispatch(UIActions.openModal(EditorModal, modalProps))
              }
            >
              Expand
            </ToolbarButton>
          </ToolbarButtonGroup>
        )}
      </Toolbar>
    );
  }

  const presentationOptions: Partial<
    ReturnType<typeof getEmbedPresentationOptions>
  > = value.embed ? getEmbedPresentationOptions(value.embed.type) : {};

  return (
    <Toolbar
      targetBlot={hoveredBlot}
      overflowMenu={
        readOnly ? undefined : (
          <ToolbarOverflowMenu onMenuOpenChange={setPersist}>
            <EmbedToolbarOverflowMenuItems
              blot={hoveredBlot}
              value={value}
              fullUrl={fullUrl}
              onViewExpanded={() => {
                dispatch(UIActions.openModal(EditorModal, modalProps));
              }}
            />
          </ToolbarOverflowMenu>
        )
      }
    >
      {!readOnly && (
        <>
          <ToolbarSpanButtonGroup
            span={formats.span}
            onSpanChange={(newSpan) => format({ span: newSpan })}
          />
          {presentationOptions.renderMode !== 'image' && (
            <ToolbarHeightButtonGroup
              disallowShortHeight={!presentationOptions.allowXSmallHeight}
              height={formats.height}
              onHeightChange={(newHeight) => format({ height: newHeight })}
            />
          )}
        </>
      )}

      {readOnly && !isDocumentLike(value) && (
        <ToolbarButtonGroup>
          <ToolbarButton
            icon={expandIcon}
            tooltip="View expanded"
            onClick={() =>
              dispatch(UIActions.openModal(EditorModal, modalProps))
            }
          />
        </ToolbarButtonGroup>
      )}

      {readOnly && isFullUrlAccessible(value) && fullUrl && (
        <ToolbarButtonGroup>
          <ToolbarButton
            icon={newTabIcon}
            tooltip="Open in new tab"
            to={fullUrl}
            newTab
          />
        </ToolbarButtonGroup>
      )}

      {readOnly && isFullUrlAccessible(value) && fullUrl && (
        <ToolbarButtonGroup>
          <ToolbarButton
            tooltip="Copy link"
            icon={copyIcon}
            onClick={async () => {
              await navigator.clipboard.writeText(fullUrl);
              dispatch(UIActions.pushToast(miscToasts.linkCopied));
            }}
          />
        </ToolbarButtonGroup>
      )}

      <ToolbarCommentButtonGroup
        blot={hoveredBlot}
        existingComment={formats.comment}
        onReactionPickerOpenChange={setPersist}
      />
    </Toolbar>
  );
};

type Context = {
  formats: Record<string, string[] | string | null | undefined>;
  value: Value;
};

const hoveredBlotListenerSetting: HoveredBlotListenerSetting<Context> = {
  isHoveredBlot(blot) {
    return (
      blot.statics.blotName === 'embed' || blot.statics.blotName === 'iframe'
    );
  },
  getContext(blot: BlockEmbed) {
    return {
      formats: blot.formats(),
      value: blot.value(),
    };
  },
};

const EmbedToolbar = () => {
  const { quill } = useQuill();

  const {
    bounds,
    hoveredBlot,
    context: { formats, value } = { formats: undefined, value: undefined },
    setPersist,
  } = useHoveredBlotListener(quill, hoveredBlotListenerSetting);

  return (
    <FloatingToolbar.Toolbar bounds={bounds}>
      {hoveredBlot instanceof BlockEmbed && value && formats && (
        <BaseEmbedToolbar
          hoveredBlot={hoveredBlot}
          formats={formats}
          format={(attributes) => {
            if (!quill) return;
            formatBlotWithoutScrolling({
              quill,
              attributes,
              blot: hoveredBlot,
            });
          }}
          setPersist={setPersist}
          value={value}
        />
      )}
    </FloatingToolbar.Toolbar>
  );
};

export default EmbedToolbar;
